import React from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import { useQuery } from '@apollo/client'
import { useOrgName } from 'src/hooks/useOrgName'
import { GET_USERS } from './queries'

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface UserSelectorProps {
  value: string
  onChange: (userId: string) => void
  label?: string
  required?: boolean
  disabled?: boolean
  size?: 'small' | 'medium'
}

const UserSelector: React.FC<UserSelectorProps> = ({
  value,
  onChange,
  label = 'User',
  required = false,
  disabled = false,
  size = 'medium',
}) => {
  const { orgName } = useOrgName()

  const { data, loading, error } = useQuery(GET_USERS, {
    variables: {
      filter: {
        orgSlug: orgName,
      },
    },
    skip: !orgName,
  })

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value)
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading users...
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Error loading users: {error.message}
      </Typography>
    )
  }

  return (
    <FormControl fullWidth size={size} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={handleChange} displayEmpty>
        <MenuItem value="">
          <em>Select a user</em>
        </MenuItem>
        {(data?.users || []).map((user) => (
          <MenuItem key={user.id} value={user.id}>
            {user.firstName} {user.lastName} ({user.email})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default UserSelector
